import { MixPanelEvents, mixpanelTrack } from "src/utils";
import { filterBySource } from "./grouping";
import { AttentionSourceType, IAttentionList, IAttentionRow } from "./types";

/**
 * Where in the product the person was when they did it.
 *
 * Just nu is the same list in four places, and "is anybody using it" has a
 * different answer in each: the bell is a glance, the page is a visit, the band
 * is the phone. Without this the numbers are one undifferentiated total and the
 * surface that is carrying the feature cannot be told from the three that are
 * not.
 */
export enum AttentionSurface {
    Bell = "bell",
    Notifications = "notifications",
    Page = "just-nu",
    MenuBand = "menu-band",
}

/**
 * The same short words the filter puts in the URL, so a chart and a link say the
 * same thing. A source added later and given no word falls back to its number
 * rather than dropping the property.
 */
function describeSource(sourceType: AttentionSourceType): string {
    return filterBySource[sourceType] ?? String(sourceType);
}

function countOverdue(rows: IAttentionRow[]): number {
    return rows.filter((row) => row.isOverdue).length;
}

/**
 * Somebody was shown a list with something in it.
 *
 * This is the denominator, and it deliberately leaves out the opens where there
 * was nothing waiting. Those could never have produced a click, so counting them
 * would only drag the rate down with impressions that were never a chance to act.
 *
 * The cost is that an empty list and an unopened bell now look the same from
 * here. "Just nu: Page viewed" fires whatever the count, so how often somebody
 * arrives to nothing is read off that instead.
 */
export function trackAttentionShown(surface: AttentionSurface, list: IAttentionList): void {
    mixpanelTrack(MixPanelEvents.JustNu_Shown, {
        surface,
        count: list.count,
        overdueCount: countOverdue(list.rows),
    });
}

/**
 * Somebody went from the summary to the whole list — "Visa alla" in the block, or
 * the band at the top of the responsive menu.
 */
export function trackAttentionOpenList(surface: AttentionSurface, list: IAttentionList): void {
    mixpanelTrack(MixPanelEvents.JustNu_OpenList, {
        surface,
        count: list.count,
        overdueCount: countOverdue(list.rows),
    });
}

/**
 * Somebody followed a row to the thing it is about. The one event that says the
 * surface did its job.
 *
 * @param position Where the row sat in the list it was clicked in, from zero. Says
 * whether anything below the first row is ever reached.
 */
export function trackAttentionRowClick(
    row: IAttentionRow,
    surface: AttentionSurface,
    position: number,
): void {
    mixpanelTrack(MixPanelEvents.JustNu_RowClicked, {
        surface,
        position,
        type: describeSource(row.sourceType),
        isOverdue: row.isOverdue,
        hasDueDate: !!row.dueAt,
    });
}

export function trackAttentionPageViewed(list: IAttentionList, filter: string): void {
    mixpanelTrack(MixPanelEvents.JustNu_PageViewed, {
        filter,
        count: list.count,
        overdueCount: countOverdue(list.rows),
    });
}

/**
 * A filter chip on the page. Says which kinds of work people came looking for,
 * which is the question behind what to build a source for next.
 */
export function trackAttentionFilter(filter: string): void {
    mixpanelTrack(MixPanelEvents.JustNu_Filter, { filter });
}
